/**
 * PostHogProvider Component
 *
 * Analytics provider that initializes PostHog and tracks page views.
 * Wraps the application to provide analytics across all pages.
 *
 * Features:
 * - Automatic page view tracking on route changes
 * - Graceful degradation if PostHog key is not configured
 * - Error handling to prevent analytics from breaking the app
 * - User identification when authenticated
 *
 * Environment Variables:
 * - REACT_APP_POSTHOG_KEY: PostHog project API key
 * - REACT_APP_POSTHOG_HOST: PostHog instance URL (optional)
 *
 * @example
 * ```jsx
 * // In your App.jsx or index.jsx
 * import PostHogProvider from './components/PostHogProvider';
 *
 * function App() {
 *   return (
 *     <PostHogProvider>
 *       <Router>
 *         <Routes>...</Routes>
 *       </Router>
 *     </PostHogProvider>
 *   );
 * }
 * ```
 */

import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import {
  initAnalytics,
  trackPageView,
  identifyUser,
  resetAnalytics,
} from '../services/analytics';
import * as Sentry from '@sentry/react';

// Check if PostHog key is configured
const POSTHOG_KEY = process.env.REACT_APP_POSTHOG_KEY || '';

/**
 * Internal component that handles page view tracking
 * @internal
 */
function PostHogPageView(): React.ReactElement | null {
  const location = useLocation();

  useEffect(() => {
    if (location.pathname) {
      try {
        const url = window.origin + location.pathname + location.search;
        trackPageView(url);
      } catch (error) {
        // Silently fail - analytics should not break the app
        if (process.env.NODE_ENV === 'development') {
          console.warn('PostHog page view tracking failed:', error);
        }
      }
    }
  }, [location.pathname, location.search]);

  return null;
}

/**
 * Internal component that handles user identification
 * @internal
 */
function PostHogUserIdentifier(): React.ReactElement | null {
  const { isSignedIn, user } = useUser();
  const previousUserIdRef = useRef(null);

  useEffect(() => {
    if (!POSTHOG_KEY) return;

    try {
      if (isSignedIn && user) {
        // Only identify if user ID changed
        if (previousUserIdRef.current !== user.id) {
          identifyUser(user.id, {
            email: user.primaryEmailAddress?.emailAddress,
            name: user.fullName || user.username,
            created_at: user.createdAt,
          });
          previousUserIdRef.current = user.id;
        }
      } else if (previousUserIdRef.current !== null) {
        // User signed out
        resetAnalytics();
        previousUserIdRef.current = null;
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('PostHog user identification failed:', error);
      }
    }
  }, [isSignedIn, user]);

  return null;
}

/**
 * PostHogProvider - Wraps app to enable analytics
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Application content to wrap
 * @returns {React.ReactNode} Provider component with analytics tracking
 */
export default function PostHogProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  // Use ref to track initialization instead of state to avoid re-render cycle
  const initializedRef = useRef(false);

  useEffect(() => {
    // Only initialize if we have a key and haven't already initialized
    if (!POSTHOG_KEY) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('PostHog key not configured. Analytics will be disabled.');
      }
      return;
    }

    if (initializedRef.current) return;

    try {
      initAnalytics();
      initializedRef.current = true;
    } catch (error) {
      // Silently fail - analytics should not break the app
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to initialize PostHog:', error);
      }
      Sentry.captureException(error);
    }
  }, []);

  return (
    <>
      {/* Only render trackers if PostHog is configured */}
      {POSTHOG_KEY && (
        <>
          <PostHogPageView />
          <PostHogUserIdentifier />
        </>
      )}
      {children}
    </>
  );
}
